import type { AgentNodeStatus, AgentTreeNode, AgentTreeView, ChatElement } from '@gitlab-org/tui';
import type { EntryAttribution } from '../backend/backend';

export const ROOT_NODE_ID = 'root';

const ROOT_DISPLAY_NAME = 'Duo';

const UNNAMED_SUBAGENT = 'subagent';

export type DelegationPhase = 'started' | 'returned' | 'failed';

export interface DelegationSignal {
  phase: DelegationPhase;
  subagentName?: string;
  subsessionId?: string;
  /** Who emitted the delegation, so a nested sub-session attaches to its delegator. */
  attribution?: EntryAttribution;
}

interface MutableNode {
  id: string;
  displayName: string;
  children: MutableNode[];
  status?: AgentNodeStatus;
  subagentName?: string;
  subsessionId?: string;
}

/**
 * Which agent each of a session's chat elements belongs to: a root agent, plus a node per
 * sub-session it delegated to. Holds element ids only — the session's flat element list
 * stays the source of truth, and this decides how to divide it up.
 */
export class AgentTree {
  #root = AgentTree.#newRoot();

  #nodesById = new Map<string, MutableNode>([[this.#root.id, this.#root]]);

  #nodeIdByElementId = new Map<string, string>();

  /** Sub-agents named by a delegation so far. Only these may own a sub-session. */
  #knownSubagentNames = new Set<string>();

  #openNodeIdByAgent = new Map<string, string>();

  #latestNodeIdByAgent = new Map<string, string>();

  #activeViewPath: string[] = [ROOT_NODE_ID];

  /** Never reset, so a rehydration cannot reuse an id that a stale element still holds. */
  #nextAnonymousId = 0;

  get hasSubagents(): boolean {
    return this.#nodesById.size > 1;
  }

  get activeViewPath(): readonly string[] {
    return this.#activeViewPath;
  }

  get activeNodeId(): string {
    return this.#activeViewPath[this.#activeViewPath.length - 1];
  }

  /** @returns false, leaving the view where it was, if the path reaches no node. */
  setActiveViewPath(path: readonly string[]): boolean {
    if (path.length === 0 || path[0] !== ROOT_NODE_ID) return false;

    let node = this.#root;
    for (const nodeId of path.slice(1)) {
      const child = node.children.find((candidate) => candidate.id === nodeId);
      if (!child) return false;
      node = child;
    }

    this.#activeViewPath = [...path];
    return true;
  }

  enterActiveChild(): boolean {
    const node = this.#nodesById.get(this.activeNodeId);
    if (!node || node.children.length === 0) return false;

    const running = [...node.children].reverse().find((child) => child.status === 'running');
    const child = running ?? node.children[node.children.length - 1];
    this.#activeViewPath = [...this.#activeViewPath, child.id];
    return true;
  }

  enterChild(childNumber: number): boolean {
    const node = this.#nodesById.get(this.activeNodeId);
    const child = node?.children[childNumber - 1];
    if (!child) return false;
    this.#activeViewPath = [...this.#activeViewPath, child.id];
    return true;
  }

  exitToParent(): boolean {
    if (this.#activeViewPath.length <= 1) return false;
    this.#activeViewPath = this.#activeViewPath.slice(0, -1);
    return true;
  }

  /**
   * Closes every sub-session still open, for turns that end without closing
   * markers. A sub-session parked on a tool approval is not dead — answering
   * the prompt resumes it — so it is marked waiting and stays open.
   *
   * @returns true when a node was parked as waiting.
   */
  settleOpenSubsessions(status: AgentNodeStatus, waitingNodeId?: string): boolean {
    let parked = false;
    for (const [agent, nodeId] of [...this.#openNodeIdByAgent.entries()]) {
      const node = this.#nodesById.get(nodeId);
      if (nodeId === waitingNodeId) {
        if (node) node.status = 'waiting';
        parked = true;
      } else {
        if (node) node.status = status;
        this.#openNodeIdByAgent.delete(agent);
      }
    }
    return parked;
  }

  /** The sub-session a started delegation opened, so the session can attach its prompt. */
  subsessionNodeId(
    subagentName: string | undefined,
    subsessionId: string | undefined,
  ): string | undefined {
    if (subagentName === undefined) return undefined;
    return this.#markedSubsession(subagentName, subsessionId)?.id;
  }

  routeToNode(elementId: string, nodeId: string): void {
    if (this.#nodesById.has(nodeId)) this.#nodeIdByElementId.set(elementId, nodeId);
  }

  reset(): void {
    this.#root = AgentTree.#newRoot();
    this.#nodesById = new Map([[this.#root.id, this.#root]]);
    this.#nodeIdByElementId.clear();
    this.#knownSubagentNames.clear();
    this.#openNodeIdByAgent.clear();
    this.#latestNodeIdByAgent.clear();
    this.#activeViewPath = [ROOT_NODE_ID];
  }

  /**
   * Markers are applied one at a time rather than paired up, since a failure has no
   * matching return.
   *
   * @returns false if a closing marker matched no sub-session, which leaves it `running`.
   */
  applyDelegation(signal: DelegationSignal): boolean {
    if (signal.phase === 'started') {
      this.#openSubsession(signal);
      return true;
    }
    return this.#closeSubsession(signal, signal.phase === 'failed' ? 'failed' : 'returned');
  }

  /** Idempotent; called again with different attribution, the element moves. */
  route(elementId: string, attribution?: EntryAttribution): void {
    const nodeId = this.#resolveNodeId(attribution);
    const node = this.#nodesById.get(nodeId) ?? this.#root;
    this.#nodeIdByElementId.set(elementId, node.id);
  }

  nodeIdForElement(elementId: string): string {
    return this.#nodeIdByElementId.get(elementId) ?? ROOT_NODE_ID;
  }

  /** The agent that owns the element; displayName is omitted for the root agent. */
  agentForElement(elementId: string): { nodeId: string; displayName?: string } {
    const nodeId = this.nodeIdForElement(elementId);
    const displayName = this.#nodesById.get(nodeId)?.displayName;
    return nodeId === ROOT_NODE_ID || displayName === undefined
      ? { nodeId }
      : { nodeId, displayName };
  }

  /** The one sub-session currently open, or undefined when none or several are. */
  get soleOpenSubsessionNodeId(): string | undefined {
    return this.#soleOpenNode()?.id;
  }

  isRouted(elementId: string): boolean {
    return this.#nodeIdByElementId.has(elementId);
  }

  toView(elements: readonly ChatElement[]): AgentTreeView {
    return {
      root: this.#toViewNode(this.#root, elements),
      activeViewPath: [...this.#activeViewPath],
    };
  }

  activeNodeElements(elements: readonly ChatElement[]): ChatElement[] {
    const node = this.#nodesById.get(this.activeNodeId) ?? this.#root;
    return this.#elementsOf(node, elements);
  }

  static #newRoot(): MutableNode {
    return {
      id: ROOT_NODE_ID,
      displayName: ROOT_DISPLAY_NAME,
      children: [],
    };
  }

  /**
   * Filters the session's own list, so the result is in chat-log order.
   */
  #elementsOf(node: MutableNode, elements: readonly ChatElement[]): ChatElement[] {
    return elements.filter((element) => this.nodeIdForElement(element.id) === node.id);
  }

  #toViewNode(node: MutableNode, elements: readonly ChatElement[]): AgentTreeNode {
    return {
      id: node.id,
      displayName: node.displayName,
      elements: this.#elementsOf(node, elements),
      children: node.children.map((child) => this.#toViewNode(child, elements)),
      ...(node.status ? { status: node.status } : {}),
    };
  }

  #openSubsession(signal: DelegationSignal): void {
    const subagentName = signal.subagentName ?? UNNAMED_SUBAGENT;
    this.#knownSubagentNames.add(subagentName);

    const existing = this.#markedSubsession(subagentName, signal.subsessionId);
    if (existing) {
      existing.status = 'running';
      this.#markOpen(existing);
      return;
    }

    const node: MutableNode = {
      id: this.#newNodeId(subagentName, signal.subsessionId),
      displayName: subagentName,
      children: [],
      status: 'running',
      subagentName,
      subsessionId: signal.subsessionId,
    };
    const parent = this.#delegatorNode(signal.attribution);
    parent.children.push(node);
    this.#nodesById.set(node.id, node);
    this.#markOpen(node);
  }

  /**
   * The delegating agent's own node, derived from the delegation entry's attribution —
   * never from the view state, which the user can move independently mid-turn. Unlike
   * #resolveNodeId this never creates a node: a delegator that resolves to no known
   * sub-session is the root agent.
   */
  #delegatorNode(attribution?: EntryAttribution): MutableNode {
    const componentName = attribution?.componentName;
    if (!componentName || !this.#knownSubagentNames.has(componentName)) return this.#root;
    return this.#markedSubsession(componentName, attribution?.subsessionId) ?? this.#root;
  }

  #markOpen(node: MutableNode): void {
    if (!node.subagentName) return;
    this.#openNodeIdByAgent.set(node.subagentName, node.id);
    this.#latestNodeIdByAgent.set(node.subagentName, node.id);
  }

  /**
   * Namespaced by sub-agent, since a sub-session id is only unique per parent. The parts
   * are JSON-encoded rather than delimiter-joined: the name comes from LLM-produced tool
   * args and may itself contain the delimiter, and two nodes whose ids alias would
   * overwrite each other in the id map, showing one agent's transcript for the other.
   */
  #newNodeId(subagentName: string, subsessionId: string | undefined): string {
    if (subsessionId !== undefined) {
      return `subsession:${JSON.stringify([subagentName, subsessionId])}`;
    }
    const anonymousId = this.#nextAnonymousId;
    this.#nextAnonymousId += 1;
    return `subsession:${JSON.stringify([subagentName])}:anon:${anonymousId}`;
  }

  #closeSubsession(signal: DelegationSignal, status: AgentNodeStatus): boolean {
    const node = this.#resolveClosingNode(signal);
    if (!node) return false;
    node.status = status;
    if (node.subagentName) this.#openNodeIdByAgent.delete(node.subagentName);
    return true;
  }

  #resolveClosingNode(signal: DelegationSignal): MutableNode | undefined {
    // Never falls back when named, so a stale return cannot close another agent's.
    if (signal.subagentName) {
      return this.#markedSubsession(signal.subagentName, signal.subsessionId);
    }
    return this.#soleOpenNode();
  }

  #soleOpenNode(): MutableNode | undefined {
    const openNodeIds = [...this.#openNodeIdByAgent.values()];
    return openNodeIds.length === 1 ? this.#nodesById.get(openNodeIds[0]) : undefined;
  }

  #resolveNodeId(attribution?: EntryAttribution): string {
    const componentName = attribution?.componentName;
    const subsessionId = attribution?.subsessionId;
    if (!componentName || !this.#knownSubagentNames.has(componentName)) return ROOT_NODE_ID;
    if (subsessionId !== undefined) return this.#subsessionNodeId(componentName, subsessionId);
    return this.#currentNode(componentName)?.id ?? ROOT_NODE_ID;
  }

  #subsessionNodeId(subagentName: string, subsessionId: string): string {
    const existing = this.#identifiedSubsession(
      subagentName,
      subsessionId,
      this.#currentNode(subagentName),
    );
    return (existing ?? this.#openSubsessionFor(subagentName, subsessionId)).id;
  }

  #markedSubsession(
    subagentName: string,
    subsessionId: string | undefined,
  ): MutableNode | undefined {
    if (subsessionId === undefined) return this.#openNode(subagentName);
    return this.#identifiedSubsession(subagentName, subsessionId, this.#openNode(subagentName));
  }

  #identifiedSubsession(
    subagentName: string,
    subsessionId: string,
    candidate: MutableNode | undefined,
  ): MutableNode | undefined {
    const identified = this.#nodeWithSubsessionId(subagentName, subsessionId);
    if (identified) return identified;
    if (!candidate || candidate.subsessionId !== undefined) return undefined;

    // eslint-disable-next-line no-param-reassign
    candidate.subsessionId = subsessionId;
    return candidate;
  }

  #openSubsessionFor(subagentName: string, subsessionId: string): MutableNode {
    this.#openSubsession({ phase: 'started', subagentName, subsessionId });
    return this.#nodeWithSubsessionId(subagentName, subsessionId) ?? this.#root;
  }

  #nodeWithSubsessionId(subagentName: string, subsessionId: string): MutableNode | undefined {
    return [...this.#nodesById.values()].find(
      (node) => node.subagentName === subagentName && node.subsessionId === subsessionId,
    );
  }

  /** The delegation to this sub-agent that is still open, else the last one there was. */
  #currentNode(subagentName: string): MutableNode | undefined {
    const nodeId =
      this.#openNodeIdByAgent.get(subagentName) ?? this.#latestNodeIdByAgent.get(subagentName);
    return nodeId ? this.#nodesById.get(nodeId) : undefined;
  }

  #openNode(subagentName: string): MutableNode | undefined {
    const nodeId = this.#openNodeIdByAgent.get(subagentName);
    return nodeId ? this.#nodesById.get(nodeId) : undefined;
  }
}
